/**
 * A real deck, built by the pipeline's own `build_cards` from
 * `cache/Computer/chapter12.pack.json` — the chapter spike 002 extracted.
 *
 * Study order is the pipeline's: definitions, then concepts, formulas, worked
 * examples, diagrams. Trimmed to a representative slice of all five card types;
 * the real chapter yields 40.
 *
 * This stands in for the generation service until Phase 1 builds it.
 */

import type { Deck } from "../cards/types";

export const sampleDeck: Deck = {
  number: 12,
  title: "Binary",
  cards: [
    {
      type: "Definition",
      front: "Binary",
      back: "A base-2 numeral system using only two digits: 0 and 1.",
      tag: null,
      mermaid: null,
    },
    {
      type: "Definition",
      front: "Binary digit (bit)",
      back: "The smallest unit of data represented in a computer; it has a single binary value, either 1 or 0.",
      tag: null,
      mermaid: null,
    },
    {
      type: "Definition",
      front: "Byte",
      back: "A basic combination of bits used to represent an item of information, typically consisting of 8 bits.",
      tag: null,
      mermaid: null,
    },
    {
      type: "Definition",
      front: "Binary information",
      back: "Information represented by only two values (e.g. a voltage or no voltage; on or off).",
      tag: null,
      mermaid: null,
    },
    {
      type: "Concept",
      front: "Why binary is used in computers",
      back: "Computers use binary because the processor contains billions of transistors that act as switches with only two states: on or off. These two states are represented by the binary digits 1 and 0.",
      tag: null,
      mermaid: null,
    },
    {
      type: "Concept",
      front: "Representing information using bits",
      back: "Bits can be combined into groups; the number of possible combinations equals 2 to the power of the number of bits used (e.g. 2 bits give 4 combinations, 3 bits give 8 combinations).",
      tag: null,
      mermaid: null,
    },
    {
      type: "Concept",
      front: "Binary place values",
      back: "In the binary system, place values increase by powers of 2 (e.g. 2⁷=128, 2⁶=64, …, 2⁰=1).",
      tag: null,
      mermaid: null,
    },
    {
      type: "Formula",
      front: "Number of combinations from n bits",
      back: "2^n",
      tag: null,
      mermaid: null,
    },
    {
      type: "Formula",
      front: "Binary place values for 8 bits",
      back: "128, 64, 32, 16, 8, 4, 2, 1 (i.e. 2^7, 2^6, 2^5, 2^4, 2^3, 2^2, 2^1, 2^0)",
      tag: null,
      mermaid: null,
    },
    {
      type: "Example",
      front: "Convert binary 10101101 to denary.",
      back: "Use the place value table; multiply each bit by its place value: (1×128)+(0×64)+(1×32)+(0×16)+(1×8)+(1×4)+(0×2)+(1×1); sum results.\n\nAnswer: 173",
      tag: null,
      mermaid: null,
    },
    {
      type: "Example",
      front: "Convert denary 213 to binary.",
      back: "Use the 8-bit place value table; from left to right: 213≥128 → 1, remainder 85; 85≥64 → 1, remainder 21; 21<32 → 0; 21≥16 → 1, remainder 5; 5<8 → 0; 5≥4 → 1, remainder 1; 1<2 → 0; 1≥1 → 1.\n\nAnswer: 11010101",
      tag: null,
      mermaid: null,
    },
    {
      type: "Diagram",
      front: "A diagram showing the conversion of a binary byte to its hexadecimal equivalent E6.",
      back: "",
      tag: "diagram",
      mermaid:
        "flowchart LR\n    B[Binary byte: 1110 0110] --> S[Split into two nibbles]\n    S --> N1[1110]\n    S --> N2[0110]\n    N1 --> H1[E]\n    N2 --> H2[6]\n    H1 --> H[Hex: E6]\n    H2 --> H",
    },
  ],
};
